import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Paper,
  TablePagination
} from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  Receipt,
  Terminal,
  Business as Location,
  AttachMoney,
  CalendarToday,
  ArrowDropDown,
  PlayArrow,
  Pause,
  Refresh,
  FiberManualRecord,
  Close
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subDays, format, startOfMonth } from 'date-fns';
import { ApiContext } from '../contexts/ApiContext';
import { transactionTypeColor } from '../utils/transactionTypes';

const Overview = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date range state - default to current month (1st of month to today)
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => new Date());
  const [tempStartDate, setTempStartDate] = useState(() => startOfMonth(new Date()));
  const [tempEndDate, setTempEndDate] = useState(() => new Date());
  
  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Live Transactions state
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveTransactions, setLiveTransactions] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [liveLastUpdated, setLiveLastUpdated] = useState(null);
  const [livePaused, setLivePaused] = useState(false);
  const [livePage, setLivePage] = useState(0);
  const [livePageSize, setLivePageSize] = useState(25);
  const [liveTotalCount, setLiveTotalCount] = useState(0);
  const [liveSince, setLiveSince] = useState(null);
  const liveIntervalRef = useRef(null);

  const { apiCall } = useContext(ApiContext);

  // Fetch live transactions with pagination
  const fetchLiveTransactions = useCallback(async (pageOverride) => {
    if (livePaused) return;

    const currentPage = pageOverride !== undefined ? pageOverride : livePage;

    try {
      setLiveLoading(true);
      let url = `/api/v1/daily-transactions/live?page=${currentPage}&pageSize=${livePageSize}`;
      if (liveSince) {
        url += `&since=${encodeURIComponent(liveSince)}`;
      }
      const data = await apiCall(url, 'GET');
      setLiveTransactions(data.transactions || []);
      setLiveTotalCount(data.totalCount || 0);
      setLiveLastUpdated(new Date());
      setLiveError(null);
    } catch (error) {
      console.error('Error fetching live transactions:', error);
      setLiveError('Failed to fetch live transactions');
    } finally {
      setLiveLoading(false);
    }
  }, [apiCall, livePaused, livePage, livePageSize, liveSince]);

  // Start/stop live polling when dialog opens/closes
  useEffect(() => {
    if (liveOpen && !livePaused) {
      // Fetch immediately
      fetchLiveTransactions();
      // Then set up interval
      liveIntervalRef.current = setInterval(fetchLiveTransactions, 10000); // 10 seconds
    }

    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, [liveOpen, livePaused, fetchLiveTransactions]);

  const handleLiveOpen = () => {
    setLiveSince(null);
    setLivePage(0);
    setLiveTotalCount(0);
    setLiveTransactions([]);
    setLiveOpen(true);
    setLivePaused(false);
  };

  const handleLiveClose = () => {
    setLiveOpen(false);
    setLivePaused(false);
    setLiveSince(null);
    setLivePage(0);
    setLiveTotalCount(0);
    if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
  };

  const toggleLivePause = () => {
    setLivePaused(!livePaused);
    if (!livePaused) {
      // Pausing - clear interval
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    } else {
      // Resuming - fetch immediately and restart interval
      fetchLiveTransactions();
      liveIntervalRef.current = setInterval(fetchLiveTransactions, 10000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'declined': return 'error';
      case 'reversal': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status, transactionType) => {
    if (transactionType === 'RWT') return 'Reversal';
    switch (status) {
      case 'approved': return 'Approved';
      case 'declined': return 'Declined';
      case 'reversal': return 'Reversal';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const applyDateRange = (newStartDate, newEndDate) => {
    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchOverviewData(newStartDate, newEndDate);
    handleClose();
  };

  const fetchOverviewData = async (dateStart = null, dateEnd = null) => {
    try {
      setLoading(true);
      
      // Use provided dates or fallback to state values
      const effectiveStartDate = dateStart || startDate;
      const effectiveEndDate = dateEnd || endDate;
      
      // Build query parameters with date range
      const params = new URLSearchParams();
      if (effectiveStartDate) {
        params.append('startDate', format(effectiveStartDate, 'yyyy-MM-dd'));
      }
      if (effectiveEndDate) {
        params.append('endDate', format(effectiveEndDate, 'yyyy-MM-dd'));
      }
      
      const endpoint = `/api/v1/overview${params.toString() ? `?${params.toString()}` : ''}`;
      const data = await apiCall(endpoint, 'GET');
      setOverviewData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setError('Failed to load overview data');
      // Mock data for development
      setOverviewData({
        totalTerminals: 25,
        totalLocations: 12,
        totalTransactions: 15420,
        totalAmount: 2847592.50,
        averageTransactionValue: 184.76,
        averageTransactionsPerTerminal: 616.8,
        averageRevenuePerLocation: 237299.38,
        topTerminals: [
          { terminalId: 'T001', locationName: 'Main Street Store', totalTransactions: 2340, totalAmount: 425000 },
          { terminalId: 'T007', locationName: 'Airport Terminal', totalTransactions: 1890, totalAmount: 380000 },
          { terminalId: 'T012', locationName: 'Downtown Plaza', totalTransactions: 1650, totalAmount: 295000 },
          { terminalId: 'T005', locationName: 'Shopping Mall', totalTransactions: 1420, totalAmount: 270000 },
          { terminalId: 'T018', locationName: 'Gas Station', totalTransactions: 980, totalAmount: 180000 }
        ],
        topLocations: [
          { locationName: 'Main Street Store', terminalCount: 3, totalTransactions: 3200, totalAmount: 580000 },
          { locationName: 'Airport Terminal', terminalCount: 2, totalTransactions: 2100, totalAmount: 420000 },
          { locationName: 'Downtown Plaza', terminalCount: 4, totalTransactions: 2800, totalAmount: 390000 },
          { locationName: 'Shopping Mall', terminalCount: 2, totalTransactions: 1900, totalAmount: 320000 },
          { locationName: 'Gas Station Chain', terminalCount: 5, totalTransactions: 2400, totalAmount: 280000 }
        ],
        volumeDistribution: [
          { category: 'Large (>$1000)', recordCount: 145, transactionCount: 890, totalAmount: 1420000, percentage: 49.9 },
          { category: 'Medium ($100-$1000)', recordCount: 320, transactionCount: 4200, totalAmount: 987000, percentage: 34.7 },
          { category: 'Small (<$100)', recordCount: 1200, transactionCount: 10330, totalAmount: 440592, percentage: 15.4 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
            {value}
          </Typography>
        </Box>
        <Typography variant="h6" component="div" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2 }}>
          Overview Dashboard
        </Typography>

        {/* Date Range Selector */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <CalendarToday color="primary" />
              </Grid>
              <Grid item>
                <Typography variant="h6" component="span">
                  Date Range
                </Typography>
              </Grid>
              <Grid item xs />
              <Grid item>
                <DatePicker
                  label="Start Date"
                  value={tempStartDate}
                  onChange={(newValue) => setTempStartDate(newValue)}
                  maxDate={tempEndDate || new Date()}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1" sx={{ mx: 1 }}>
                  to
                </Typography>
              </Grid>
              <Grid item>
                <DatePicker
                  label="End Date"
                  value={tempEndDate}
                  onChange={(newValue) => setTempEndDate(newValue)}
                  minDate={tempStartDate}
                  maxDate={new Date()}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setStartDate(tempStartDate);
                    setEndDate(tempEndDate);
                    fetchOverviewData(tempStartDate, tempEndDate);
                  }}
                  disabled={!tempStartDate || !tempEndDate || loading}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
            
            {/* Right-aligned Quick Select and Status */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Quick select:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClick}
                endIcon={<ArrowDropDown />}
                sx={{ minWidth: 140 }}
              >
                Date Ranges
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'date-range-button',
                }}
                PaperProps={{
                  sx: { minWidth: 160 }
                }}
              >
                <MenuItem onClick={() => {
                  const today = new Date();
                  const sevenDaysAgo = subDays(today, 7);
                  applyDateRange(sevenDaysAgo, today);
                }}>
                  Last 7 Days
                </MenuItem>
                <MenuItem onClick={() => {
                  const today = new Date();
                  const thirtyDaysAgo = subDays(today, 30);
                  applyDateRange(thirtyDaysAgo, today);
                }}>
                  Last 30 Days
                </MenuItem>
                <MenuItem onClick={() => {
                  const today = new Date();
                  const ninetyDaysAgo = subDays(today, 90);
                  applyDateRange(ninetyDaysAgo, today);
                }}>
                  Last 90 Days
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                  const today = new Date();
                  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  applyDateRange(startOfMonth, today);
                }}>
                  This Month
                </MenuItem>
                <MenuItem onClick={() => {
                  const today = new Date();
                  const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                  applyDateRange(startOfLastMonth, endOfLastMonth);
                }}>
                  Last Month
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                  const today = new Date();
                  const startOfYear = new Date(today.getFullYear(), 0, 1);
                  applyDateRange(startOfYear, today);
                }}>
                  Year to Date
                </MenuItem>
              </Menu>
            </Box>
            
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleLiveOpen}
                startIcon={<FiberManualRecord sx={{ fontSize: 12, animation: 'pulse 1.5s infinite' }} />}
                sx={{
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                    '100%': { opacity: 1 }
                  }
                }}
              >
                Live Transactions
              </Button>
              <Typography variant="body2" color="text.secondary">
                Showing data from {format(startDate, 'MMM dd, yyyy')} to {format(endDate, 'MMM dd, yyyy')}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Key Performance Indicators */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Withdrawals"
            value={formatCurrency(overviewData?.totalAmount || 0)}
            icon={<AttachMoney />}
            color="success"
            subtitle={`${formatNumber(overviewData?.totalTransactions || 0)} transactions`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Terminals"
            value={overviewData?.totalTerminals || 0}
            icon={<Terminal />}
            color="primary"
            subtitle={`Avg ${formatNumber(overviewData?.averageTransactionsPerTerminal || 0)} trans/terminal`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Locations"
            value={overviewData?.totalLocations || 0}
            icon={<Location />}
            color="secondary"
            subtitle={`Avg ${formatCurrency(overviewData?.averageRevenuePerLocation || 0)} withdrawals/location`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Average Transaction"
            value={formatCurrency(overviewData?.averageTransactionValue || 0)}
            icon={<Receipt />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Transactions"
            value={formatNumber(overviewData?.totalTransactions || 0)}
            icon={<TrendingUp />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Settlement Records"
            value={formatNumber(overviewData?.totalRecords || 0)}
            icon={<AccountBalance />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Top Performers Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top Terminals */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Top Performing Terminals
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Terminal</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Withdrawals</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overviewData?.topTerminals?.map((terminal, index) => (
                      <TableRow key={terminal.terminalId}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Chip 
                              label={index + 1} 
                              size="small" 
                              color={index === 0 ? 'success' : index === 1 ? 'warning' : 'default'}
                              sx={{ mr: 1, minWidth: 24 }}
                            />
                            {terminal.terminalId}
                          </Box>
                        </TableCell>
                        <TableCell>{terminal.locationName}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formatCurrency(terminal.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(terminal.totalTransactions)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Locations */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Top Performing Locations
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell align="center">Terminals</TableCell>
                      <TableCell align="right">Withdrawals</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overviewData?.topLocations?.map((location, index) => (
                      <TableRow key={location.locationName}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Chip 
                              label={index + 1} 
                              size="small" 
                              color={index === 0 ? 'success' : index === 1 ? 'warning' : 'default'}
                              sx={{ mr: 1, minWidth: 24 }}
                            />
                            {location.locationName}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={location.terminalCount} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formatCurrency(location.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(location.totalTransactions)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transaction Volume Distribution */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Transaction Volume Distribution
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                {overviewData?.volumeDistribution?.map((category) => (
                  <Grid item xs={12} md={4} key={category.category}>
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {category.category}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {category.percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={category.percentage} 
                        sx={{ height: 8, borderRadius: 4, mb: 2 }}
                      />
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Withdrawals
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(category.totalAmount)}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" color="text.secondary">
                            Transactions
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {formatNumber(category.transactionCount)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Container>

      {/* Live Transactions Dialog */}
      <Dialog
        open={liveOpen}
        onClose={handleLiveClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '85vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FiberManualRecord
              sx={{
                fontSize: 16,
                color: livePaused ? 'grey.500' : 'success.main',
                animation: livePaused ? 'none' : 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                  '100%': { opacity: 1 }
                }
              }}
            />
            <Typography variant="h6">
              Live Transactions {livePaused && '(Paused)'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {liveLastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Last updated: {format(liveLastUpdated, 'HH:mm:ss')}
              </Typography>
            )}
            <Tooltip title={livePaused ? 'Resume' : 'Pause'}>
              <IconButton onClick={toggleLivePause} size="small">
                {livePaused ? <PlayArrow /> : <Pause />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Now">
              <IconButton onClick={fetchLiveTransactions} size="small" disabled={liveLoading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleLiveClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {liveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {liveError}
            </Alert>
          )}

          {liveLoading && liveTransactions.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : liveTransactions.length === 0 ? (
            <Alert severity="info">
              No transactions found for today. Transactions will appear here as they occur.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time (ET)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Terminal</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Surcharge</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Card Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveTransactions.map((txn, index) => (
                    <TableRow
                      key={`${txn.terminalId}-${txn.rawTime}-${txn.sequenceNumber}-${index}`}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                        transition: 'background-color 0.3s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {txn.time}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={txn.locationName}>
                          {txn.locationName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {txn.terminalId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.transactionType}
                          size="small"
                          variant="outlined"
                          color={transactionTypeColor(txn.transactionType)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: txn.status === 'approved' ? 'success.main' :
                                   txn.status === 'reversal' ? 'warning.main' : 'text.primary'
                          }}
                        >
                          ${txn.amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          ${txn.surcharge.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {txn.cardType || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(txn.status, txn.transactionType)}
                          size="small"
                          color={getStatusColor(txn.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={txn.responseDescription}
                        >
                          {txn.responseDescription || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={liveTotalCount}
            rowsPerPage={livePageSize}
            page={livePage}
            onPageChange={(e, newPage) => {
              setLivePage(newPage);
              fetchLiveTransactions(newPage);
            }}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setLivePageSize(newSize);
              setLivePage(0);
              fetchLiveTransactions(0);
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {liveTotalCount} transaction{liveTotalCount !== 1 ? 's' : ''} today
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Auto-refreshes every 10 seconds
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLiveClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Overview;